import { useMemo } from 'react';
import { z } from 'zod';
import { ModuleSubCategory } from '@/apolloClient.types';
import { useUserSession } from '@/contexts/UserSession';
import { defaultTtlMs, useHybridUrlSessionState } from '@/hooks/useHybridUrlSessionState';

export const traineeAssessmentUiStateSchema = z
    .object({
        programId: z.string().nullable(),
        assessmentRunId: z.string().nullable(),
        subCategory: z.nativeEnum(ModuleSubCategory),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
    })
    .strict();

export type TraineeAssessmentUiState = z.infer<typeof traineeAssessmentUiStateSchema>;

export interface TraineeAssessmentUiActions {
    setProgramId: (programId: string | null) => void;
    setProgramIdWithAssessmentRunId: (args: { programId: string | null; assessmentRunId: string | null }) => void;
    setAssessmentRunId: (assessmentRunId: string | null) => void;
    setSubCategory: (subCategory: ModuleSubCategory) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    clearFilters: () => void;
}

export const useTraineeAssessmentUiState = (defaultState: TraineeAssessmentUiState) => {
    const { sessionId } = useUserSession();
    const namespace = useMemo(() => {
        const sessionKey = sessionId ?? 'anonymous';
        return `traineeAssessment.${sessionKey}`;
    }, [sessionId]);

    const { state, setState, key } = useHybridUrlSessionState<TraineeAssessmentUiState>({
        namespace,
        schema: traineeAssessmentUiStateSchema,
        defaultState,
        urlKeyParam: 'ui',
        ensureKeyOnMount: true,
        persistOnMount: true,
        ttlMs: defaultTtlMs,
        version: 1,
    });

    const actions = useMemo<TraineeAssessmentUiActions>(
        () => ({
            setProgramId: programId => {
                setState(prev => ({
                    ...prev,
                    programId,
                    // Selecting a new program resets the run selection + paging
                    assessmentRunId: null,
                    page: 1,
                }));
            },
            setProgramIdWithAssessmentRunId: ({ programId, assessmentRunId }) => {
                setState(prev => ({
                    ...prev,
                    programId,
                    assessmentRunId,
                    page: 1,
                }));
            },
            setAssessmentRunId: assessmentRunId => {
                setState(prev => ({
                    ...prev,
                    assessmentRunId,
                    page: 1,
                }));
            },
            setSubCategory: subCategory => {
                setState(prev => ({
                    ...prev,
                    subCategory,
                    page: 1,
                }));
            },
            setPage: page => setState(prev => ({ ...prev, page })),
            setPageSize: pageSize =>
                setState(prev => ({
                    ...prev,
                    pageSize,
                    page: 1,
                })),
            clearFilters: () =>
                setState(prev => ({
                    ...prev,
                    programId: null,
                    assessmentRunId: null,
                    page: 1,
                })),
        }),
        [setState]
    );

    return { state, actions, key };
};
